import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Trophy, 
  Clock,
  Medal,
  Crown
} from 'lucide-react';
import Navbar from '../components/Navbar';
import BadgeArt from '../components/BadgeArt';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function LeaderboardContent({ loading, entries, formatTime, getMedalIcon }) {
  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="w-8 h-8 border-4 border-[#8B5A3C]/30 border-t-[#8B5A3C] rounded-full animate-spin" />
      </div>
    );
  }
  if (entries.length === 0) {
    return (
      <div className="text-center py-8 text-[var(--text-disabled)]">
        <Trophy className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>No entries yet. Be the first to complete a puzzle!</p>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {entries.map((entry, index) => (
        <div 
          key={entry.id}
          className={`flex items-center justify-between p-3 rounded ${
            index < 3 ? 'bg-[var(--hover-bg-subtle)]' : ''
          }`}
          data-testid={`leaderboard-entry-${index}`}
        >
          <div className="flex items-center gap-4">
            <div className="w-8 flex justify-center">
              {getMedalIcon(index)}
            </div>
            <div>
              <p className="text-[var(--text-primary)] font-medium flex items-center gap-2">
                <span>{entry.user_name || 'Anonymous'}</span>
                {entry.top_badges && entry.top_badges.length > 0 && (
                  <span className="inline-flex items-center gap-0.5" title={entry.top_badges.map(b => b.name).join(' · ')}>
                    {entry.top_badges.map((b) => (
                      <BadgeArt
                        key={b.id}
                        achievementId={b.id}
                        category={b.category}
                        locked={false}
                        size={20}
                      />
                    ))}
                  </span>
                )}
              </p>
              <p className="text-xs text-[var(--text-disabled)]">
                Level {entry.difficulty_level} - {entry.difficulty_name}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-right">
            <Clock className="w-4 h-4 text-[var(--text-secondary)]" />
            <span className="font-mono text-lg text-[var(--text-primary)]">
              {formatTime(entry.time_elapsed)}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function LeaderboardPage() {
  const [leaderboard, setLeaderboard] = useState([]);
  const [dailyLeaderboard, setDailyLeaderboard] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLeaderboards = async () => {
      try {
        const [allRes, dailyRes] = await Promise.all([
          axios.get(`${API_URL}/api/leaderboard`, { 
            params: selectedLevel ? { difficulty_level: selectedLevel } : {},
            withCredentials: true 
          }),
          axios.get(`${API_URL}/api/leaderboard/daily`, { withCredentials: true })
        ]);
        setLeaderboard(allRes.data);
        setDailyLeaderboard(dailyRes.data);
      } catch (err) {
        toast.error('Failed to load leaderboard');
      } finally {
        setLoading(false);
      }
    };
    fetchLeaderboards();
  }, [selectedLevel]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getMedalIcon = (index) => {
    if (index === 0) return <Medal className="w-5 h-5 text-[#FFD700]" />;
    if (index === 1) return <Medal className="w-5 h-5 text-[#C0C0C0]" />;
    if (index === 2) return <Medal className="w-5 h-5 text-[#CD7F32]" />;
    return <span className="w-5 h-5 flex items-center justify-center text-[var(--text-disabled)] font-mono">{index + 1}</span>;
  };

  const currentLeaderboard = activeTab === 'daily' ? dailyLeaderboard : leaderboard;

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background-main)' }}>
      <Navbar />
      
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[var(--text-primary)] mb-2 flex items-center justify-center gap-3">
            <Trophy className="w-8 h-8 text-[#FFD700]" />
            Leaderboard
          </h1>
          <p className="text-[var(--text-secondary)]">
            Top solvers ranked by fastest completion times
          </p>
        </div>

        {/* Tabs */}
        <div className="flex justify-center gap-2 mb-6">
          <Button
            variant={activeTab === 'all' ? 'default' : 'outline'}
            onClick={() => setActiveTab('all')}
            className={activeTab === 'all' ? 'bg-[#8B5A3C] text-white' : 'border-[var(--border-subtle)] text-[var(--text-primary)] hover:bg-[var(--hover-bg)]'}
            data-testid="tab-all"
          >
            All Time
          </Button>
          <Button
            variant={activeTab === 'daily' ? 'default' : 'outline'}
            onClick={() => setActiveTab('daily')}
            className={activeTab === 'daily' ? 'bg-[#8B5A3C] text-white' : 'border-[var(--border-subtle)] text-[var(--text-primary)] hover:bg-[var(--hover-bg)]'}
            data-testid="tab-daily"
          >
            Today
          </Button>
        </div>

        {/* Level Filter */}
        <div className="flex justify-center gap-2 mb-6 flex-wrap">
          <Button
            variant={selectedLevel === null ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setSelectedLevel(null)}
            className={selectedLevel === null ? 'bg-[#8B5A3C] text-white' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}
          >
            All Levels
          </Button>
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(level => (
            <Button
              key={level}
              variant={selectedLevel === level ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setSelectedLevel(level)}
              className={selectedLevel === level ? 'bg-[#8B5A3C] text-white' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}
            >
              {level}
            </Button>
          ))}
        </div>

        {/* Leaderboard Table */}
        <Card className="bg-[var(--background-surface)] border-[var(--border-subtle)]">
          <CardHeader>
            <CardTitle className="text-[var(--text-primary)] font-heading flex items-center gap-2">
              <Crown className="w-5 h-5 text-[#8B5A3C]" />
              {activeTab === 'daily' ? "Today's Top Solvers" : 'All Time Best'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <LeaderboardContent 
              loading={loading} 
              entries={currentLeaderboard} 
              formatTime={formatTime} 
              getMedalIcon={getMedalIcon} 
            />
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
